import type { ReactElement } from 'react';
import { t } from '@fetmode/i18n';
import { Link, useParams } from 'react-router-dom';

import { INFO_CONTENT, type InfoSection } from './info-content.js';
import './InfoPage.css';

function SectionBlock({ section }: { section: InfoSection }): ReactElement {
  return (
    <div className="info-page__section">
      {section.heading ? <h2 className="info-page__heading">{section.heading}</h2> : null}
      {section.paragraphs?.map((paragraph) => (
        <p key={paragraph.slice(0, 40)} className="info-page__body">
          {paragraph}
        </p>
      ))}
      {section.bullets ? (
        <ul className="info-page__list">
          {section.bullets.map((bullet) => (
            <li key={bullet.slice(0, 40)}>{bullet}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function InfoPage(): ReactElement {
  const { slug } = useParams<{ slug: string }>();
  const content = slug ? INFO_CONTENT[slug] : undefined;

  if (!content) {
    return (
      <section className="info-page">
        <div className="info-page__inner">
          <h1 className="info-page__title">{t('common.info.notFoundTitle')}</h1>
          <p className="info-page__lead">{t('common.info.notFoundBody')}</p>
          <Link to="/" className="info-page__back">
            {t('common.info.backHome')}
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="info-page">
      <div className="info-page__inner">
        <h1 className="info-page__title">{content.title}</h1>
        <p className="info-page__lead">{content.lead}</p>

        {content.updated ? (
          <p className="info-page__updated">
            {t('common.info.updatedLabel')}: {content.updated}
          </p>
        ) : null}

        {content.draft ? <p className="info-page__notice">{t('common.info.draftNotice')}</p> : null}

        {content.sections.map((section, index) => (
          <SectionBlock key={section.heading ?? `section-${String(index)}`} section={section} />
        ))}

        {content.contactEmail ? (
          <p className="info-page__contact">
            {t('common.info.contactPrompt')}{' '}
            <a href={`mailto:${content.contactEmail}`}>{content.contactEmail}</a>
          </p>
        ) : null}

        <Link to="/" className="info-page__back">
          {t('common.info.backHome')}
        </Link>
      </div>
    </section>
  );
}
